/**
 * Session 30.11 — Native app boot flow (Android APK + Windows EXE only).
 *
 *   1. Splash    — polished NFC Solutions loading screen while the device
 *                  id, local snapshot and connectivity warm up.
 *   2. Business  — Business Code (prefilled with the remembered code;
 *                  validated against /api/public/business/{code}/skeleton,
 *                  falls back to the cached local business when offline).
 *   3. Dashboard — pick Bar / Kitchen / Staff / Retailer / Owner-Manager.
 *                  The last-used dashboard is remembered and shown first
 *                  with a "Continue" affordance, but every dashboard stays
 *                  one tap away — the device is never locked to a role.
 *
 * Replaces the old /install "choose dashboard" wizard for installed apps.
 * The website keeps its direct /staff, /kitchen, ... URLs untouched.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ChefHat, ClipboardList, Loader2, Shield, ShoppingBag, Wine, Building2, ChevronLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  NATIVE_DASHBOARDS,
  getDeviceId,
  getStoredBusinessCode,
  setStoredBusinessCode,
  getLastUsedDashboard,
  setLastUsedDashboard,
} from "@/lib/device";
import { getBusiness as getLocalBusiness } from "@/lib/localDb";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SPLASH_MIN_MS = 1400;

const ICONS = { bar: Wine, kitchen: ChefHat, staff: ClipboardList, retail: ShoppingBag, owner: Shield };

function Splash({ status }) {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[hsl(var(--background))] px-6" data-testid="native-splash">
      {/* soft radial glow */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--primary)/0.14),transparent_60%)]" aria-hidden="true" />
      <div className="relative flex flex-col items-center gap-6 animate-in fade-in zoom-in-95 duration-500">
        <div className="flex h-28 w-28 items-center justify-center rounded-[32px] border border-border/60 bg-card shadow-[0_20px_60px_-20px_hsl(var(--primary)/0.45)]">
          <img src="/nfcs-logo.png" alt="NFC Solutions" className="h-20 w-20 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
        <div className="text-center">
          <p className="font-['Space_Grotesk'] text-3xl font-semibold tracking-tight">NFC Solutions</p>
          <p className="mt-1 text-sm text-muted-foreground">Restaurant &amp; retail operating system</p>
        </div>
        <div className="mt-2 h-1 w-48 overflow-hidden rounded-full bg-secondary">
          <div className="h-full w-1/2 animate-[nfcsSplashBar_1.2s_ease-in-out_infinite] rounded-full bg-[hsl(var(--primary))]" />
        </div>
        <p className="text-xs text-muted-foreground" data-testid="native-splash-status">{status}</p>
      </div>
      <style>{`@keyframes nfcsSplashBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
      <p className="absolute bottom-6 text-[11px] text-muted-foreground/70">Powered by NFC Solutions</p>
    </div>
  );
}

export default function NativeBootPage({ getSession = () => null }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("splash");
  const [splashStatus, setSplashStatus] = useState("Starting up…");
  const [code, setCode] = useState(() => { try { return getStoredBusinessCode() || ""; } catch (_) { return ""; } });
  const [business, setBusiness] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const lastUsed = useMemo(() => { try { return getLastUsedDashboard(); } catch (_) { return ""; } }, []);
  const inputRef = useRef(null);

  // ── Step 1: splash + warm-up ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const started = Date.now();
    (async () => {
      try { getDeviceId(); } catch (_) { /* noop */ }
      setSplashStatus("Preparing your workspace…");
      try {
        const local = await getLocalBusiness();
        if (local && !cancelled) setBusiness((b) => b || { name: local.name, slug: local.slug || local.code, offline: true });
      } catch (_) { /* noop */ }
      const wait = Math.max(0, SPLASH_MIN_MS - (Date.now() - started));
      setTimeout(() => { if (!cancelled) setStep("code"); }, wait);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (step === "code") setTimeout(() => { try { inputRef.current?.focus(); } catch (_) { /* noop */ } }, 80);
  }, [step]);

  // ── Step 2: business code ─────────────────────────────────────
  const submitCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const clean = String(code || "").trim().toLowerCase();
    if (!clean) { setError("Enter your business code."); return; }
    setChecking(true);
    setError("");
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/business/${encodeURIComponent(clean)}/skeleton`, { timeout: 8000 });
      const biz = r.data?.business || {};
      setBusiness({ name: biz.name || clean, slug: biz.slug || clean, logo_url: biz.logo_url || null });
      setStoredBusinessCode(clean);
      setStep("pick");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) { setError("We couldn't find a business with that code. Check it and try again."); }
      else {
        // Offline / server unreachable → accept a code we've used before.
        const remembered = (() => { try { return getStoredBusinessCode(); } catch (_) { return ""; } })();
        if (remembered && remembered === clean) {
          setBusiness((b) => b || { name: clean, slug: clean, offline: true });
          setStep("pick");
        } else {
          setError("No connection right now. Connect to the internet once to verify a new business code.");
        }
      }
    } finally {
      setChecking(false);
    }
  };

  // ── Step 3: dashboard pick ────────────────────────────────────
  const choose = (dash) => {
    try { setLastUsedDashboard(dash.key); } catch (_) { /* noop */ }
    const sess = (() => { try { return getSession(dash.key); } catch (_) { return null; } })();
    if (sess && sess.token && !sess._pending) {
      navigate(`${dash.path}/dashboard`, { replace: true });
    } else {
      navigate(`${dash.path}?business=${encodeURIComponent(business?.slug || code)}`, { replace: true });
    }
  };

  const ordered = useMemo(() => {
    const list = [...NATIVE_DASHBOARDS];
    if (!lastUsed) return list;
    const idx = list.findIndex((d) => d.key === lastUsed);
    if (idx > 0) { const [d] = list.splice(idx, 1); list.unshift(d); }
    return list;
  }, [lastUsed]);

  if (step === "splash") return <Splash status={splashStatus} />;

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-[hsl(var(--background))] px-4 py-10" data-testid={`native-boot-${step}`}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.10),transparent_55%)]" aria-hidden="true" />
      <div className="relative w-full max-w-lg animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="mb-6 flex items-center justify-center gap-3">
          <img src="/nfcs-logo.png" alt="" className="h-9 w-9 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <p className="font-['Space_Grotesk'] text-xl font-semibold tracking-tight">NFC Solutions</p>
        </div>

        {step === "code" ? (
          <form onSubmit={submitCode} className="rounded-[28px] border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary text-[hsl(var(--primary))]"><Building2 className="h-5 w-5" /></div>
              <div>
                <h1 className="text-lg font-semibold">Business code</h1>
                <p className="text-sm text-muted-foreground">Step 1 of 2 · which restaurant is this device for?</p>
              </div>
            </div>
            <Input
              ref={inputRef}
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(""); }}
              placeholder="e.g. harbor-bistro"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className={`h-12 rounded-xl text-base ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
              data-testid="native-business-code-input"
            />
            {error ? <p className="mt-2 text-sm font-medium text-destructive" role="alert" data-testid="native-business-code-error">{error}</p> : null}
            {business?.name && !error ? (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="native-business-code-hint">Last used on this device: <span className="font-semibold text-foreground">{business.name}</span></p>
            ) : null}
            <Button type="submit" className="mt-5 h-12 w-full rounded-xl bg-[hsl(var(--accent))] text-base font-semibold text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={checking || !code.trim()} data-testid="native-business-code-continue">
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Continue
            </Button>
          </form>
        ) : null}

        {step === "pick" ? (
          <div className="rounded-[28px] border border-border bg-card p-5 shadow-sm sm:p-8">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold">Choose a dashboard</h1>
                <p className="text-sm text-muted-foreground">
                  Step 2 of 2 · <span className="font-medium text-foreground">{business?.name || code}</span>
                  {business?.offline ? <span className="ml-1 text-amber-700 dark:text-amber-400">(offline — using saved details)</span> : null}
                </p>
              </div>
              <button type="button" onClick={() => setStep("code")} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid="native-change-business">
                <ChevronLeft className="h-3.5 w-3.5" /> Change
              </button>
            </div>
            <div className="grid min-w-0 grid-cols-1 gap-2.5" data-testid="native-dashboard-list">
              {ordered.map((d) => {
                const Icon = ICONS[d.key] || ClipboardList;
                const isLast = d.key === lastUsed;
                const sess = (() => { try { return getSession(d.key); } catch (_) { return null; } })();
                const signedIn = Boolean(sess && sess.token && !sess._pending);
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => choose(d)}
                    className={`group flex w-full min-w-0 max-w-full items-center gap-3 overflow-hidden rounded-2xl border p-3.5 text-left transition-colors sm:gap-4 sm:p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] ${isLast ? "border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/5 hover:bg-[hsl(var(--primary))]/10" : "border-border bg-background hover:bg-secondary"}`}
                    data-testid={`native-dashboard-${d.key}`}
                  >
                    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${isLast ? "bg-[hsl(var(--primary))] text-primary-foreground" : "bg-secondary text-[hsl(var(--primary))]"}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-base font-semibold">{d.label}</p>
                        {isLast ? <span className="rounded-full bg-[hsl(var(--primary))]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--primary))]" data-testid="native-last-used-badge">Last used</span> : null}
                        {signedIn ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300"><Check className="h-3 w-3" /> Signed in</span> : null}
                      </div>
                      <p className="truncate text-sm text-muted-foreground">{d.blurb}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-center text-[11px] text-muted-foreground">You can switch dashboards any time from the sign-in screen.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
